import { useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import { Button, Box, Stack, Link } from '@mui/material'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { useAppDispatch, useAppSelector, usePayRazorpay } from '../../../hooks'
import { createPaynow, createPayLater, createVerifySubscribedPlan } from '../../../services'
import { currencyFormatter, loadScript } from '../../../utils'
import { showAlertAction } from '../../../store/alerts'
import {
  fetchPendingBillsAction,
  hidePaymentDuePopupAction,
} from '../../../store/pendingBills'

const TypographyDesc = styled(Typography)(
  () => `
        padding-bottom: 15px ; 
   `
)

const PaymentDuePopupContent = () => {
  const dispatchAction = useAppDispatch()
  const [makeRazorPayment] = usePayRazorpay()
  const [jsLoadStatus, setJsLoadStatus] = useState(false)
  const [payLaterLoading, setPayLaterLoading] = useState(false)
  const { data } = useAppSelector((state) => state.pendingBills)
  // Load the javascript
  useEffect(() => {
    loadScript('https://checkout.razorpay.com/v1/checkout.js')
      .then(() => {
        setJsLoadStatus(true)
      })
      .catch((errr) => {
        console.error('Razorpay SDK failed to load. Are you online?', errr)
        setJsLoadStatus(false)
      })
  }, [])
  useEffect(() => {
    dispatchAction(fetchPendingBillsAction(true))
  }, [])
  const paymentFailed = function () {
    dispatchAction(
      showAlertAction({ message: 'Payment failed', severity: 'error' })
    )
  }
  const paymentSuccess = async (response) => {
    const verifyRequest = {
      signature: response.razorpay_signature,
      payment_id: response.razorpay_payment_id,
    }
    const verifyResponse = await createVerifySubscribedPlan(verifyRequest)
    if (verifyResponse?.data || verifyResponse?.success) {
      dispatchAction(hidePaymentDuePopupAction())
      dispatchAction(fetchPendingBillsAction(false))
    } else {
      dispatchAction(
        showAlertAction({
          message: verifyResponse?.message || 'Opps something went wrong',
          severity: 'error',
        })
      )
    }
  }
  const displayRazorpay = async (id: number, month: string) => {
    const payNowResponse = await createPaynow(id)
    if (!payNowResponse?.success || !payNowResponse?.data) {
      dispatchAction(
        showAlertAction({
          message: payNowResponse?.message || 'Opps something went wrong',
          severity: 'error',
        })
      )
      return false
    }
    if (!jsLoadStatus) {
      return false
    }
    const subscribePlanData = { ...payNowResponse?.data, description: month }
    makeRazorPayment(subscribePlanData, paymentSuccess, paymentFailed)
  }
  const handlePayLater = async (id: number) => {
    setPayLaterLoading(true)
    try {
      const payLaterResponse = await createPayLater(id)
      if (payLaterResponse?.success) {
        dispatchAction(hidePaymentDuePopupAction())
        dispatchAction(fetchPendingBillsAction(false))
      } else {
        dispatchAction(
          showAlertAction({
            message: payLaterResponse?.message || 'Something went wrong',
            severity: 'error',
          })
        )
      }
    } catch (error) {
      dispatchAction(
        showAlertAction({
          message: 'Failed to process pay later request',
          severity: 'error',
        })
      )
    } finally {
      setPayLaterLoading(false)
    }
  }
  const handleCancel = () => {
    dispatchAction(hidePaymentDuePopupAction())
  }
  const handleWhatsAppClick = () => {
    window.open(
      'https://web.whatsapp.com/send?phone=917976757979&text=Hi, I have a question about billing.',
      '_blank'
    )
  }
  const [pendingBill] = data
  return (
    <Card variant="outlined">
      <CardContent sx={{ m: 3, textAlign: 'center' }}>
        {/* Billing Period */}
        <Typography fontSize={16} component="div" align={'center'} mt={2}>
          Billing Period
        </Typography>
        <Typography fontSize={16} mt={1} fontWeight={'bold'} color="text.primary">
          {pendingBill?.month}
        </Typography>

        {/* Total Billing */}
        <Typography fontSize={16} component="div" mt={3}>
          Your total billing for this period
        </Typography>
        <Typography fontSize={16} fontWeight={'bold'} mt={1}>
          {currencyFormatter.format(pendingBill?.consumption)}
        </Typography>

        {/* App Charges */}
        <Typography fontSize={16} component="div" mt={2}>
          App charges for this month
        </Typography>
        <Box sx={{ mt: 1, mb: 3 }}>
          <span
            style={{
              textDecoration: 'line-through',
              color: 'red',
              fontSize: 12,
              marginRight: 5,
            }}
          >
            {currencyFormatter.format(pendingBill?.amount)}
          </span>
          <span
            style={{
              color: 'green',
              fontWeight: 'bold',
              fontSize: 16,
            }}
          >
            {currencyFormatter.format(pendingBill?.amountAfterDiscount)}
          </span>
        </Box>

        {/* WhatsApp Contact Message */}
        <Box
          sx={{
            mt: 2,
            mb: 3,
            p: 2,
            backgroundColor: '#f0f9ff',
            borderRadius: 1,
            border: '1px solid #bae6fd',
          }}
        >
          <Typography fontSize={14} color="text.primary" sx={{ mb: 1 }}>
            Do you want{' '}
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                color: '#0284c7',
                backgroundColor: '#e0f2fe',
                px: 0.5,
                borderRadius: 0.5,
              }}
            >
              discounted yearly plan
            </Typography>
            ? or any doubt on billing, contact us on{' '}
            <Link
              component="button"
              onClick={handleWhatsAppClick}
              sx={{
                color: '#25D366',
                fontWeight: 700,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: 15,
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: '#25D36615',
                  borderRadius: 0.5,
                  px: 0.5,
                },
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 20 }} />
              WhatsApp +91 7976757979
            </Link>
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 3, mb: 2 }}
        >
          <Button
            id="buy_plan_button"
            variant="contained"
            sx={{
              backgroundColor: 'green',
              '&:hover': {
                backgroundColor: '#2e7d32',
              },
            }}
            onClick={() => displayRazorpay(pendingBill?.id, pendingBill?.month)}
          >
            Pay {currencyFormatter.format(pendingBill?.amountAfterDiscount)} now
          </Button>
          {pendingBill?.isPayLaterAvailable && (
            <Button
              id="pay_later_button"
              variant="contained"
              sx={{
                backgroundColor: '#6366f1',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#4f46e5',
                },
                '&:disabled': {
                  backgroundColor: '#a5b4fc',
                  color: 'white',
                },
              }}
              onClick={() => handlePayLater(pendingBill?.id)}
              disabled={payLaterLoading}
            >
              {payLaterLoading ? 'Processing...' : 'Pay Later'}
            </Button>
          )}
        </Stack>

        {/* Cancel Button */}
        <Box sx={{ mt: 2 }}>
          <Button
            variant="text"
            onClick={handleCancel}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}
export default PaymentDuePopupContent
